import { useState, useRef, useEffect, type ReactNode } from 'react'
import { ChevronDown } from 'lucide-react'
import { cn } from '@/utils/cn'

export interface DropdownOption {
  value: string
  label: string
  description?: string
  icon?: ReactNode
  /** If true, shows as locked and triggers onLockedClick instead of onChange */
  locked?: boolean
  /** Badge element to show on the right (e.g., LockBadge) */
  badge?: ReactNode
}

interface DropdownProps {
  label?: string
  value: string
  onChange: (value: string) => void
  options: DropdownOption[]
  className?: string
  /** Adds a subtle gradient border glow effect */
  glow?: boolean
  /** Called when a locked option is clicked */
  onLockedClick?: (value: string) => void
  /** Disable the dropdown */
  disabled?: boolean
}

export function Dropdown({
  label,
  value,
  onChange,
  options,
  className,
  glow,
  onLockedClick,
  disabled = false,
}: DropdownProps) {
  const [isOpen, setIsOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const selected = options.find((o) => o.value === value)

  // Close on click outside
  useEffect(() => {
    if (!isOpen) return

    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isOpen])

  return (
    <div ref={containerRef} className="relative h-full w-full">
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={cn(
          "relative flex flex-col justify-between px-3 py-2 md:px-4 md:py-3 rounded-xl text-white min-w-[140px] w-full h-full transition-all duration-300",
          glow 
            ? "bg-[#0d0d0d] shadow-[0_0_15px_-3px_rgba(139,92,246,0.4)]"
            : "bg-white/[0.03] hover:bg-white/[0.05]",
          disabled && "opacity-50 cursor-not-allowed",
          className
        )}
      >
        {/* Top row: Label left, Chevron right */}
        <div className="flex items-center justify-between">
          {label && <span className="text-xs text-[#666]">{label}</span>}
          <ChevronDown
            className={cn("size-4 text-[#666] transition-transform shrink-0", isOpen && "rotate-180")}
          />
        </div>
        
        {/* Value row with icon */}
        <div className="flex items-center gap-2">
          {/* Hide icon on mobile */}
          {selected?.icon && <span className="hidden md:block text-[#888]">{selected.icon}</span>}
          <span className="text-sm">{selected?.label}</span>
        </div>
      </button>

      {/* Dropdown menu */}
      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-1 rounded-xl bg-[#111] border border-white/[0.1] shadow-xl z-50 overflow-hidden">
          {options.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => {
                if (option.locked && onLockedClick) {
                  onLockedClick(option.value)
                  setIsOpen(false)
                } else if (!option.locked) {
                  onChange(option.value)
                  setIsOpen(false)
                }
              }}
              className={cn(
                "w-full px-4 py-2.5 text-left transition-colors flex items-center gap-2.5",
                value === option.value
                  ? "bg-white/[0.08] text-white"
                  : "text-[#888] hover:bg-white/[0.05] hover:text-white",
                option.locked && "opacity-70"
              )}
            >
              {/* Hide icon on mobile in dropdown menu too */}
              {option.icon && <span className="hidden md:block shrink-0">{option.icon}</span>}
              <div className="flex-1">
                <div className="text-sm">{option.label}</div>
                {option.description && (
                  <div className="text-xs text-[#555]">{option.description}</div>
                )}
              </div>
              {/* Badge (lock icon) */}
              {option.badge && <span className="shrink-0">{option.badge}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
